import type {
  ChessAction,
  ChessState,
} from "@cloudnest/augmented-chess-engine";
import { tryApplyAction } from "@cloudnest/augmented-chess-engine";
import { type ChessReplayEvent, toChessReplayEvent } from "./event-adapter";
import type { ChessReplayFixtureEvent } from "./replay-fixtures.generated";

export interface ChessReplaySnapshot {
  readonly index: number;
  readonly event: ChessReplayFixtureEvent;
  readonly replayEvent: ChessReplayEvent;
  readonly state: ChessState;
}

function requireState(
  state: ChessState | null,
  eventType: ChessReplayEvent["eventType"],
): ChessState {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

function actionForReplayEvent(event: ChessReplayEvent): ChessAction | null {
  switch (event.eventType) {
    case "action":
    case "select_augmentation":
    case "move":
    case "resign":
      return event.payload;
    case "init":
    case "game_over":
      return null;
  }
}

function applyReplayEvent(
  state: ChessState | null,
  event: ChessReplayEvent,
): ChessState {
  if (event.eventType === "init") {
    return event.payload.state;
  }

  const current = requireState(state, event.eventType);
  const action = actionForReplayEvent(event);
  if (action === null || action.type === "RESIGN") {
    return current;
  }

  const result = tryApplyAction(current, action);
  if (!result.success) {
    throw new Error(
      `${event.eventType} rejected: ${result.error ?? "Validation failed"}`,
    );
  }
  return result.state;
}

export function buildChessReplaySnapshots(
  events: readonly ChessReplayFixtureEvent[],
): readonly ChessReplaySnapshot[] {
  const snapshots: ChessReplaySnapshot[] = [];
  let state: ChessState | null = null;

  for (const event of events) {
    const replayEvent = toChessReplayEvent(event);
    state = applyReplayEvent(state, replayEvent);
    snapshots.push({
      index: snapshots.length,
      event,
      replayEvent,
      state,
    });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty Chess event log");
  }

  return snapshots;
}
